import { useLocation, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { findService } from "../data/services";
import { CITIES, findCity } from "../data/cities";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import {
    getNearestCities,
    getLocalServiceParagraphs,
    getLocalServiceTips,
    getLocalTestimonials,
    getExtraLocalFaq,
} from "../lib/cityServiceContent";
import PannesGrid from "../components/sections/PannesGrid";
import BrandsCloud from "../components/sections/BrandsCloud";
import FaqAccordion from "../components/sections/FaqAccordion";
import PageFinalCta from "../components/sections/PageFinalCta";
import RelatedPannes from "../components/sections/RelatedPannes";
import ImageGallery from "../components/sections/ImageGallery";
import { APPLIANCE_IMAGES } from "../data/images";

/**
 * URL pattern: /reparateur-{serviceKey}-{citySlug}
 * Examples:
 *   /reparateur-lave-linge-neuilly-sur-seine
 *   /reparateur-frigo-boulogne-billancourt
 * Service keys: lave-linge, lave-vaisselle, frigo, four, seche-linge
 */

const SERVICE_KEY_TO_SLUG = {
    "lave-linge": "reparateur-lave-linge-paris",
    "lave-vaisselle": "reparateur-lave-vaisselle-paris",
    "frigo": "reparateur-frigo-paris",
    "four": "reparation-four-paris",
    "seche-linge": "reparation-seche-linge-paris",
};

const SERVICE_LABELS = {
    "lave-linge": { article: "lave-linge", display: "Lave-linge", verb: "réparation de lave-linge", action: "Réparateur lave-linge" },
    "lave-vaisselle": { article: "lave-vaisselle", display: "Lave-vaisselle", verb: "réparation de lave-vaisselle", action: "Réparateur lave-vaisselle" },
    "frigo": { article: "frigo", display: "Frigo", verb: "réparation de frigo et congélateur", action: "Réparateur frigo" },
    "four": { article: "four", display: "Four", verb: "réparation de four et micro-ondes", action: "Réparation four" },
    "seche-linge": { article: "sèche-linge", display: "Sèche-linge", verb: "réparation de sèche-linge", action: "Réparation sèche-linge" },
};

/**
 * Parse the pathname into { serviceKey, citySlug } or null.
 * Trick: city slugs can contain hyphens, so we look for the LONGEST service key that prefixes the path.
 */
function parsePath(pathname) {
    const path = pathname.replace(/^\/reparateur-|^\/reparation-/, "");
    const keys = Object.keys(SERVICE_KEY_TO_SLUG).sort((a, b) => b.length - a.length);
    for (const key of keys) {
        if (path.startsWith(`${key}-`)) {
            const citySlug = path.slice(key.length + 1);
            return { serviceKey: key, citySlug };
        }
    }
    return null;
}

function buildLocalFaqs(serviceKey, city, service, labels) {
    const extraFaq = getExtraLocalFaq(serviceKey, city);
    return [
        {
            q: `Combien de temps pour intervenir sur un ${labels.article} à ${city.name} ?`,
            a: `Nous intervenons à ${city.name} (${city.postal}) sous 24 à 48 h dans la majorité des cas. Pour les urgences (fuite, frigo HS, dégât des eaux), une intervention le jour même est généralement possible selon planning.`,
        },
        {
            q: `Quels quartiers de ${city.name} couvrez-vous ?`,
            a: `L'ensemble de la commune : ${city.neighborhoods.join(", ")}. Accès rapide via ${city.access}.`,
        },
        {
            q: `Réparez-vous les ${labels.article} encastrés à ${city.name} ?`,
            a: `Oui. Une grande partie des cuisines à ${city.name} sont équipées d'électroménager intégré (${city.habitat}). Nos techniciens sont formés pour intervenir sans abîmer les meubles ni les façades.`,
        },
        {
            q: `Quel est le tarif d'une intervention à ${city.name} ?`,
            a: `Déplacement et devis gratuit par téléphone (déduits si réparation acceptée). Forfait main-d'œuvre à partir de 99 € pour ${labels.article}, hors pièces. Devis ferme avant toute intervention, pas de frais cachés.`,
        },
        {
            q: `Quelles marques de ${labels.article} réparez-vous à ${city.name} ?`,
            a: `Toutes les marques du marché : ${service.brands.join(", ")} et plus. Nos techniciens interviennent sur appareils d'entrée de gamme comme sur le haut de gamme (Miele, Smeg, Liebherr).`,
        },
        ...(extraFaq ? [extraFaq] : []),
    ];
}

function buildJsonLd({ crumbs, h1, labels, city, description, localFaqs, pathname }) {
    return {
        "@context": "https://schema.org",
        "@graph": [
            breadcrumbJsonLd(crumbs),
            {
                "@type": "WebPage",
                "@id": `${siteUrl(pathname)}#webpage`,
                "url": siteUrl(pathname),
                "name": h1,
                "inLanguage": "fr-FR",
                "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": [".speakable-headline", ".speakable-voice-faq"],
                },
            },
            {
                "@type": "Service",
                "name": h1,
                "serviceType": labels.verb,
                "provider": { "@id": siteUrl("/#business") },
                "areaServed": {
                    "@type": "City",
                    "name": city.name,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": city.name,
                        "postalCode": city.postal,
                        "addressCountry": "FR",
                    },
                    "geo": {
                        "@type": "GeoCoordinates",
                        "latitude": city.coords.lat,
                        "longitude": city.coords.lon,
                    },
                },
                "description": description,
                "offers": {
                    "@type": "Offer",
                    "priceCurrency": "EUR",
                    "price": "49",
                    "priceSpecification": {
                        "@type": "PriceSpecification",
                        "price": "49",
                        "priceCurrency": "EUR",
                        "description": "Déplacement et diagnostic, déduit si réparation acceptée",
                    },
                },
            },
            {
                "@type": "FAQPage",
                "mainEntity": localFaqs.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
            },
            {
                "@type": "LocalBusiness",
                "@id": siteUrl(`${pathname}#localbusiness`),
                "name": `Electrocase – ${labels.action} ${city.name}`,
                "image": siteUrl("/og-image.jpg"),
                "telephone": PHONE_TEL.replace("tel:", ""),
                "priceRange": "€€",
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": city.name,
                    "postalCode": city.postal,
                    "addressCountry": "FR",
                },
                "geo": {
                    "@type": "GeoCoordinates",
                    "latitude": city.coords.lat,
                    "longitude": city.coords.lon,
                },
                "areaServed": city.name,
            },
        ],
    };
}

export default function CityServicePage() {
    const location = useLocation();
    const parsed = parsePath(location.pathname);
    if (!parsed) return <Navigate to="/" replace />;
    const { serviceKey, citySlug } = parsed;
    const service = findService(SERVICE_KEY_TO_SLUG[serviceKey]);
    const city = findCity(citySlug);
    if (!service || !city) return <Navigate to="/" replace />;

    const labels = SERVICE_LABELS[serviceKey];
    const h1 = `${labels.action} à ${city.name}`;
    const title = `${labels.action} ${city.name} – 24 h | Electrocase`;
    const description = `${labels.action} à ${city.name} à domicile sous 24 h, toutes marques. ${labels.display}, devis gratuit, garantie 18 mois. ☎ ${PHONE_DISPLAY}.`;
    const keywords = `${labels.action.toLowerCase()} ${city.name.toLowerCase()}, dépannage ${labels.article} ${city.name.toLowerCase()}, ${labels.verb} ${city.name.toLowerCase()}, ${labels.article} en panne ${city.name.toLowerCase()}`;
    const url = siteUrl(location.pathname);

    const crumbs = [
        { name: "Accueil", to: "/" },
        { name: labels.display, to: `/${service.slug}` },
        { name: city.name },
    ];

    const localFaqs = buildLocalFaqs(serviceKey, city, service, labels);
    const localParagraphs = getLocalServiceParagraphs(serviceKey, city);
    const localTips = getLocalServiceTips(serviceKey, city);
    const testimonials = getLocalTestimonials(serviceKey, city);
    const jsonLd = buildJsonLd({ crumbs, h1, labels, city, description, localFaqs, pathname: location.pathname });

    const heroBullets = [
        `Diagnostic à domicile dans ${city.postal}`,
        `Pièces ${service.brands.slice(0, 3).join(", ")} et plus en stock véhicule`,
        `Habitat type : ${city.habitat}`,
        `Accès : ${city.access}`,
        "Garantie 18 mois pièces & main-d'œuvre",
    ];

    const sameServiceOtherCities = getNearestCities(city.slug, 8);
    const otherServicesSameCity = Object.keys(SERVICE_KEY_TO_SLUG).filter((k) => k !== serviceKey);

    return (
        <>
            <Seo title={title} description={description} keywords={keywords} jsonLd={jsonLd} canonical={url} />

            {/* HERO */}
            <section className="relative overflow-hidden grain">
                <div className="absolute -top-32 -right-20 w-[420px] h-[420px] bg-[var(--amber)]/20 rounded-full blur-3xl pointer-events-none" aria-hidden />
                <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-10 pb-14 relative">
                    <Breadcrumbs items={crumbs} />
                    <div className="grid lg:grid-cols-12 gap-10 items-center mt-6">
                        <div className="lg:col-span-7">
                            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--navy)]/80 bg-white/70 border border-[var(--line)] rounded-full px-3 py-1.5">
                                <i className="fa-solid fa-location-dot text-[var(--amber)]" /> {city.name} · {city.postal} · {city.dept}
                            </p>
                            <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-5 speakable-headline">{h1}</h1>
                            <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-2xl">{city.intro}</p>
                            {/* Bloc de contenu géolocalisé unique — combat les duplicate-content
                                warnings de Google Search Console en exposant habitat, quartiers
                                et points-de-repère propres à chaque ville/arrondissement. */}
                            <div className="mt-4 max-w-2xl text-[var(--ink-soft)] space-y-2.5">
                                <p>
                                    <strong>{city.name}</strong> ({city.positionHabitat}) se caractérise par son habitat : <em>{city.habitat}</em>.
                                    Nos techniciens y interviennent quotidiennement et connaissent les contraintes locales — accès, type d&apos;immeubles, électroménager dominant.
                                </p>
                                {city.landmarks && city.landmarks.length > 0 && (
                                    <p>
                                        À proximité de <strong>{city.landmarks.slice(0, 4).join(", ")}</strong>, notre véhicule technique stationne à courte distance pour réduire le délai d&apos;intervention. {labels.action} prise en charge généralement <strong>sous 24 h</strong> en semaine.
                                    </p>
                                )}
                            </div>
                            <p className="mt-3 text-base text-[var(--ink-soft)] max-w-2xl">{labels.action} à {city.name} : <strong>diagnostic à domicile, devis gratuit, intervention sous 24 à 48 h</strong>. Toutes marques.</p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-5 py-3.5 rounded-full" data-testid="city-cta-phone">
                                    <i className="fa-solid fa-phone-volume" /> Appeler {PHONE_DISPLAY}
                                </a>
                                <Link to="/contact" className="btn-outline-navy inline-flex items-center gap-2 px-5 py-3.5 rounded-full font-semibold">
                                    <i className="fa-regular fa-pen-to-square" /> Devis gratuit
                                </Link>
                            </div>
                        </div>
                        <aside className="lg:col-span-5">
                            <div className="card-soft p-6">
                                <h2 className="heading text-lg font-bold flex items-center gap-2"><i className="fa-solid fa-clipboard-check text-[var(--amber)]" /> Intervention à {city.name}</h2>
                                <ul className="mt-4 space-y-2.5 text-sm">
                                    {heroBullets.map((t) => (
                                        <li key={t} className="flex items-start gap-2"><i className="fa-solid fa-check text-[var(--amber)] mt-1" /> <span>{t}</span></li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <PannesGrid
                pannes={service.pannes}
                title={`Pannes courantes de ${labels.article} à ${city.name}`}
                intro="Diagnostic précis sur place, intervention ciblée."
            />

            <RelatedPannes applianceKey={serviceKey} cityName={city.name} />

            {/* CONTEXTE LOCAL (contenu unique par ville × service) */}
            <section className="bg-white border-y border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="divider-amber mb-5" />
                        <h2 className="heading text-2xl lg:text-3xl font-extrabold">{labels.display} à {city.name} : ce qu&apos;il faut savoir</h2>
                        <div className="mt-6 space-y-4 text-[var(--ink-soft)] leading-relaxed">
                            {localParagraphs.map((p, i) => (
                                <p key={`para-${p.slice(0, 30)}-${i}`}>{p}</p>
                            ))}
                        </div>
                    </div>
                    <aside className="lg:col-span-5">
                        <div className="card-soft p-6">
                            <h3 className="heading text-lg font-bold flex items-center gap-2">
                                <i className="fa-solid fa-lightbulb text-[var(--amber)]" /> Conseils d&apos;entretien à {city.name}
                            </h3>
                            <ul className="mt-4 space-y-2.5 text-sm">
                                {localTips.map((tip) => (
                                    <li key={tip} className="flex items-start gap-2">
                                        <i className="fa-solid fa-arrow-right text-[var(--amber)] mt-1" />
                                        <span>{tip}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                </div>
            </section>

            {/* QUARTIERS */}
            <section className="bg-[var(--paper)] border-y border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                    <div className="divider-amber mb-5" />
                    <h2 className="heading text-2xl lg:text-3xl font-extrabold">Quartiers desservis à {city.name}</h2>
                    <p className="mt-3 text-[var(--ink-soft)]">Une équipe locale qui connaît chaque rue, chaque résidence — pour un service au plus près de chez vous.</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
                        {city.neighborhoods.map((n) => (
                            <div key={n} className="flex items-center gap-2 bg-white rounded-lg px-4 py-3 border border-[var(--line)] text-sm">
                                <i className="fa-solid fa-location-dot text-[var(--amber)]" /> {n}
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-sm text-[var(--ink-soft)]"><i className="fa-solid fa-train-subway mr-2 text-[var(--amber)]" /> Accès rapide : {city.access}.</p>
                </div>
            </section>

            <BrandsCloud
                brands={service.brands}
                title={`Toutes marques de ${labels.article} réparées à ${city.name}`}
            />

            {/* AVIS CLIENTS LOCAUX */}
            <section className="bg-white border-y border-[var(--line)]">
                <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                    <div className="divider-amber mb-5" />
                    <h2 className="heading text-2xl lg:text-3xl font-extrabold">Avis clients — {labels.action} {city.name}</h2>
                    <p className="mt-3 text-[var(--ink-soft)]">Témoignages de clients récents dans votre quartier.</p>
                    <div className="grid md:grid-cols-3 gap-5 mt-8">
                        {testimonials.map((t, i) => (
                            <article key={`${t.name}-${i}`} className="card-soft p-6 flex flex-col">
                                <div className="flex items-center gap-1 text-[var(--amber)] text-sm">
                                    {[0, 1, 2, 3, 4].map((s) => <i key={`star-${s}`} className="fa-solid fa-star" />)}
                                </div>
                                <p className="mt-3 text-sm text-[var(--ink-soft)] leading-relaxed flex-1">« {t.text} »</p>
                                <div className="mt-4 pt-4 border-t border-[var(--line)]">
                                    <p className="text-sm font-bold heading text-[var(--navy)]">{t.name}</p>
                                    <p className="text-xs text-[var(--ink-soft)]">{t.neighborhood} · {city.name}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <FaqAccordion
                title={`FAQ – ${labels.display} à ${city.name}`}
                faqs={localFaqs}
            />

            {/* CROSS-LINKS */}
            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-14">
                <div className="grid lg:grid-cols-2 gap-10">
                    <div>
                        <div className="divider-amber mb-5" />
                        <h2 className="heading text-xl lg:text-2xl font-extrabold">Autres services à {city.name}</h2>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            {otherServicesSameCity.map((k) => {
                                const otherService = findService(SERVICE_KEY_TO_SLUG[k]);
                                const otherLabels = SERVICE_LABELS[k];
                                return (
                                    <Link key={k} to={buildCityServicePath(k, city.slug)} className="card-soft p-4 block">
                                        <i className={`${otherService.icon} text-[var(--amber)]`} />
                                        <p className="heading font-bold text-sm mt-2">{otherLabels.action}</p>
                                        <p className="text-xs text-[var(--ink-soft)]">{city.name}</p>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <div className="divider-amber mb-5" />
                        <h2 className="heading text-xl lg:text-2xl font-extrabold">{labels.action} dans les villes voisines</h2>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            {sameServiceOtherCities.map((c) => (
                                <Link key={c.slug} to={buildCityServicePath(serviceKey, c.slug)} className="card-soft p-4 block">
                                    <i className="fa-solid fa-location-dot text-[var(--amber)]" />
                                    <p className="heading font-bold text-sm mt-2">{labels.display} {c.name}</p>
                                    <p className="text-xs text-[var(--ink-soft)]">{c.postal} · {Math.round(c._distKm)} km</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <PageFinalCta
                title={`Une panne de ${labels.article} à ${city.name} ?`}
                subtitle="Diagnostic à domicile, devis transparent, garantie 18 mois."
            />

            <ImageGallery
                title={`${labels.action} ${city.name} — Photos`}
                images={APPLIANCE_IMAGES[serviceKey] || []}
            />
        </>
    );
}

/**
 * Build URL path for a given service key + city slug.
 * "four" and "seche-linge" use the "reparation-" prefix; others use "reparateur-".
 */
export function buildCityServicePath(serviceKey, citySlug) {
    const prefix = (serviceKey === "four" || serviceKey === "seche-linge") ? "reparation" : "reparateur";
    return `/${prefix}-${serviceKey}-${citySlug}`;
}

/**
 * All ville × service paths to register in the router and sitemap.
 */
export function allCityServicePaths() {
    const keys = Object.keys(SERVICE_KEY_TO_SLUG);
    const out = [];
    for (const k of keys) {
        for (const c of CITIES) {
            out.push(buildCityServicePath(k, c.slug));
        }
    }
    return out;
}
